<template>

  <div class="LeadForm">

    <form action="none" @submit.prevent="submitForm">

      <SupportMessage :content="localeStrings.supportMessage" />

      <h3 class="heading">{{ localeStrings.heading }}</h3>

      <div class="formFields">
        <EmailField v-bind="{ disabled }" ref="email" />
        <PhoneField v-bind="{ disabled }" ref="phone" />
        <MessageField v-bind="{ disabled }" ref="message" />
      </div>

      <div class="formFooter">
        <button class="button" v-bind="{ disabled }">{{ localeStrings.submitButtonText }}</button>
        <div class="statusMessage" v-show="submissionStatus === 'failed'">
          {{ localeStrings.submissionErrorText }}
        </div>
      </div>

    </form>

    <div v-if="submissionStatus === 'submitted'" class="thankYou">
      <div class="thankYouTitle">{{ localeStrings.submissionThankYou.title }}</div>
      <span v-html="thankYouCheckmarkSvg" class="thankYouCheckmark"></span>
      <div class="thankYouMessage">{{ localeStrings.submissionThankYou.message }}</div>
    </div>

  </div>

</template>

<script>
import localeInfo from '~/data/localeInfo.json'
import strings from './strings.json'

import submitForm from '~/lib/submitForm.js'

import thankYouCheckmarkSvg from '../images/thank-you-checkmark.svg?raw'

import EmailField from './EmailField.vue'
import PhoneField from './PhoneField.vue'
import MessageField from './MessageField.vue'
import SupportMessage from './SupportMessage.vue'

export default {
  components: {
    EmailField,
    PhoneField,
    MessageField,
    SupportMessage,
  },
  props: {
    segment: String,
    whenSubmitted: Function,
  },
  inject: ['locale'],
  data() {
    return {
      submissionStatus: 'unsubmitted',
      thankYouCheckmarkSvg,
    }
  },
  computed: {
    disabled() {
      return this.submissionStatus === 'submitting'
    },
    fieldRefs() {
      return {
        email: this.$refs.email,
        phone: this.$refs.phone,
        message: this.$refs.message,
      }
    },
    localeStrings() {
      return strings[this.locale]
    },
  },
  methods: {
    allCheckInvalid() {
      Object.values(this.fieldRefs).forEach(
        function (fieldRef) {
          fieldRef.checkInvalid()
        }
      )
    },
    submitForm() {
      if (typeof window !== 'undefined') {
        this.allCheckInvalid()
        const valid = Object.values(this.fieldRefs).every(({ status }) => status === 'valid')
        if (valid) {
          const submissionData = {
            requestType: 'sales',
            country: localeInfo[this.locale].countryName,
            comments: this.fieldRefs.message.value,
            email: this.fieldRefs.email.value,
            phone: this.fieldRefs.phone.value,
            digitalLeadType: "Quick message",
          }
          this.submissionStatus = 'submitting'
          submitForm({ variant: this.segment, locale: this.locale }, submissionData)
            .then((response) => {
              if (response.ok) {
                this.submissionStatus = 'submitted'
                this.whenSubmitted?.()
              }
              else {
                this.submissionStatus = 'failed'
              }
            })
            .catch(() => {
              this.submissionStatus = 'failed'
            })
        }
      }
    },
  }
}
</script>

<style scoped>

.LeadForm {
  display: grid;
}

.LeadForm > * {
  grid-column: 1;
  grid-row: 1;
}

.heading {
  margin-top: var(--spacing-4);
  font-weight: 600;
  font-size: var(--font-size-3);
  color: var(--text-color-black);
}

.button {

  border-radius: 4px;
  padding: var(--spacing-4) var(--spacing-7);
  background-color: var(--bg-color-dark-blue);
  width: 100%;

  font-weight: 500;
  font-size: var(--font-size-2);
  line-height: 1;
  color: var(--text-color-white);

  transition: all 0.15s;
}

.button:not(:disabled):hover {
  background-color: #0081d6;
}

.button:disabled {
  opacity: 0.25;
}

.formFooter {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
  align-items: center;
}

.statusMessage {
  font-weight: 400;
  font-size: var(--font-size-1);
  line-height: 1.3;
  color: var(--text-color-black);
}

.formFields {
  margin-top: var(--spacing-3);
  display: flex;
  flex-direction: column;
  row-gap: var(--spacing-2);
}

.formFields + .formFooter {
  margin-top: var(--spacing-7);
}

.thankYou {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  row-gap: var(--spacing-7);
  background-color: var(--bg-color-white);
  text-align: center;
}

.thankYouTitle {
  font-weight: 600;
  font-size: var(--font-size-4);
  color: var(--text-color-black);
}

.thankYouCheckmark {
  width: 50px;
}

.thankYouMessage {
  font-weight: 500;
  font-size: var(--font-size-1);
  color: var(--text-color-black);
}
</style>
